import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyOptionModule as MatOptionModule } from '@angular/material/legacy-core';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacyPaginatorModule as MatPaginatorModule } from '@angular/material/legacy-paginator';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacyRadioModule as MatRadioModule } from '@angular/material/legacy-radio';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacySlideToggleModule as MatSlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { MatLegacyTabsModule as MatTabsModule } from '@angular/material/legacy-tabs';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { AngularEditorModule } from '@kolkov/angular-editor';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule } from '@ngx-translate/core';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { QRCodeModule } from 'angularx-qrcode';
import { BlockUIModule } from 'ng-block-ui';
import { NgxDocViewerModule } from 'ngx-doc-viewer';
import { NgxPaginationModule } from 'ngx-pagination';
import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { NgxPermissionsService } from 'ngx-permissions';
import { NgPipesModule } from 'ngx-pipes';

import { AtMentionModule } from '@app/core/_common/at-mention/at-mention.module';
import { TagInputModule } from '@app/core/_common/ngx-chips';
import { CoreModule } from '@app/core/core.module';
import { PartialsModule } from '@app/layout-sidebar/partials/partials.module';
import { DefectiveReportsComponent } from '@app/modules/inventory/defective-reports/defective-reports.component';
import { DefectiveReportsFormComponent } from '@app/modules/inventory/defective-reports-form/defective-reports-form.component';
import { DefectiveReportsFormRfqsComponent } from '@app/modules/inventory/defective-reports-form-rfqs/defective-reports-form-rfqs.component';
import { PickListTotalComponent } from '@app/modules/inventory/inventory/adjustment-entry-form/pick-list-total.component';
import { ReceivingComponent } from '@app/modules/inventory/receiving/receiving.component';
import { SharedModule } from '@app/shared/shared.module';
import { LuminousV2Module } from 'v2/luminous-v2.module';
import { PricingMatrixPipe } from 'v2/modules/pricing/pipes/pricing-matrix.pipe';
import { DirectiveModule } from 'v2/modules/shared/directives/directive.module';
import { PipeModule } from 'v2/modules/shared/pipes/pipe.module';
import {
  FeatureFlagUtilService,
  PermissionUtilService,
} from 'v2/modules/shared/services/util';
import { FeatureModule } from 'v2/shared/feature/feature.module';

import { MatFileUploadModule } from '../../core/_common/mat-file-upload/projects';
import { FeatureToggleService } from '../../shared/services/feature-toggle.service';
import { BulkTagsComponent } from '../bulk-tags/bulk-tags.component';
import { CostLayersModule } from '../cost-layers/cost-layers.module';
import { MessagingModule } from '../messaging/messaging.module';

import { RfqKitItemsWarehouseComponent } from './../../../v2/modules/rfq/components/rfq-kit-items-warehouse/rfq-kit-items-warehouse.component';
import { TransformKeysPipe } from './../../../v2/modules/shared/pipes/transform-keys.pipe';
import { UomDisplayPipe } from './../../shared/pipes/uom-display.pipe';
import { ArchivedRfqsComponent } from './archived-rfqs/archived-rfqs.component';
import { BulkUpdateClientRfqFormComponent } from './bulk-update-client-rfq-form/bulk-update-client-rfq-form.component';
import { ClientPriceOverridesComponent } from './client-price-overrides/client-price-overrides.component';
import { ConsolidateMasterSkuComponent } from './consolidate-master-sku/consolidate-master-sku.component';
import { CreateClientRfqVariantComponent } from './create-client-rfq-variant/create-client-rfq-variant.component';
import { EditRfqVariantsComponent } from './edit-rfq-variants/edit-rfq-variants.component';
import { AdjustmentEntriesComponent } from './inventory/adjustment-entries/adjustment-entries.component';
import { AdjustmentEntryFormComponent } from './inventory/adjustment-entry-form/adjustment-entry-form.component';
import { AdjustmentEntryFormPopupComponent } from './inventory/adjustment-entry-form-popup/adjustment-entry-form-popup.component';
import { AdjustmentEntryStockLocationsFormComponent } from './inventory/adjustment-entry-stock-locations-form/adjustment-entry-stock-locations-form.component';
import { ChannelShippingComponent } from './inventory/channel-shipping/channel-shipping.component';
import { ExportTemplateRfqComponent } from './inventory/export-template-rfq/export-template-rfq.component';
import { ExportTemplateRfqPopupFormComponent } from './inventory/export-template-rfq-popup-form/export-template-rfq-popup-form.component';
import { GridStocksComponent } from './inventory/grid-stocks/grid-stocks.component';
import { InvListComponent } from './inventory/inv-list/inv-list.component';
import { RequestForQuotationListComponent } from './inventory/request-for-quotation-list/request-for-quotation-list.component';
import { RetailPricesComponent } from './inventory/retail-prices/retail-prices.component';
import { SkuDuplicateReportComponent } from './inventory/sku-duplicate-report/sku-duplicate-report.component';
import { SkuStockDetailsComponent } from './inventory/sku-stock-details/sku-stock-details.component';
import { SkuStockDetailsChannelOrdersComponent } from './inventory/sku-stock-details-channel-orders/sku-stock-details-channel-orders.component';
import { SkuStockDetailsPurchaseOrdersComponent } from './inventory/sku-stock-details-purchase-orders/sku-stock-details-purchase-orders.component';
import { StockTransferPopupFormComponent } from './inventory/stock-transfer-popup-form/stock-transfer-popup-form.component';
import { StockTransfersListComponent } from './inventory/stock-transfers-list/stock-transfers-list.component';
import { StocksComponent } from './inventory/stocks/stocks.component';
import { InventoryRoutingModule } from './inventory-routing.module';
import { InventoryTransferOrderComponent } from './inventory-transfer-order/inventory-transfer-order.component';
import { NegotiatePriceFormComponent } from './negotiate-price-form/negotiate-price-form.component';
import { NdAttachmentPopupComponent } from './new-designs/nd-attachment-popup/nd-attachment-popup.component';
import { NdBulkUpdatePopupFormComponent } from './new-designs/nd-bulk-update-popup-form/nd-bulk-update-popup-form.component';
import { NdCardNotesComponent } from './new-designs/nd-card-notes/nd-card-notes.component';
import { NdCardSubtasksComponent } from './new-designs/nd-card-subtasks/nd-card-subtasks.component';
import { NdListViewComponent } from './new-designs/nd-list-view/nd-list-view.component';
import { NdMasterPipelineViewComponent } from './new-designs/nd-master-pipeline-view/nd-master-pipeline-view.component';
import { NdPipelinePopupComponent } from './new-designs/nd-pipeline-popup/nd-pipeline-popup.component';
import { NdPipelineViewComponent } from './new-designs/nd-pipeline-view/nd-pipeline-view.component';
import { NdPopupFormComponent } from './new-designs/nd-popup-form/nd-popup-form.component';
import { NdProductPageComponent } from './new-designs/nd-product-page/nd-product-page.component';
import { NdViewCardPopupComponent } from './new-designs/nd-view-card-popup/nd-view-card-popup.component';
import { NdViewDesignComponent } from './new-designs/nd-view-design/nd-view-design.component';
import { NewProductPageComponent } from './new-product/new-product-page/new-product-page.component';
import { NpListViewComponent } from './new-product/np-list-view/np-list-view.component';
import { NpPipelineViewComponent } from './new-product/np-pipeline-view/np-pipeline-view.component';
import { NpRfqPipelinePopupComponent } from './new-product/np-rfq-pipeline-popup/np-rfq-pipeline-popup.component';
import { NpTagsPopupComponent } from './new-product/np-tags-popup/np-tags-popup.component';
import { PriceNegotiationsComponent } from './price-negotiations/price-negotiations.component';
import { QuotationMessagesComponent } from './quotation-messages/quotation-messages.component';
import { QuotationPopupFormComponent } from './quotation-popup-form/quotation-popup-form.component';
import { QuotationRequestsComponent } from './quotation-requests/quotation-requests.component';
import { QuotationVariantFormComponent } from './quotation-variant-form/quotation-variant-form.component';
import { QuotesPageComponent } from './quotes-page/quotes-page.component';
import { RequoteRequestsComponent } from './requote-requests/requote-requests.component';
import { RfqAttachments } from './rfq-attachments/rfq-attachments.component';
import { RfqClientProfileComponent } from './rfq-client-profile/rfq-client-profile.component';
import { RfqClientsPopupFormComponent } from './rfq-clients-popup-form/rfq-clients-popup-form.component';
import { RfqDetailsComponent } from './rfq-details/rfq-details.component';
import { RfqDetailsCogsComponent } from './rfq-details-cogs/rfq-details-cogs.component';
import { RfqDetailsCostComponent } from './rfq-details-cost/rfq-details-cost.component';
import { RfqDetailsKitItemsComponent } from './rfq-details-kit-items/rfq-details-kit-items.component';
import { RfqDetailsPageComponent } from './rfq-details-page/rfq-details-page.component';
import { RfqDetailsQtyLogsComponent } from './rfq-details-qty-logs/rfq-details-qty-logs.component';
import { RfqDetailsQuotationsComponent } from './rfq-details-quotations/rfq-details-quotations.component';
import { RfqDetailsSalesComponent } from './rfq-details-sales/rfq-details-sales.component';
import { RfqFactoryAdminAssignmentComponent } from './rfq-factory-admin-assignment/rfq-factory-admin-assignment.component';
import { RfqFormVariantsComponent } from './rfq-form-variants/rfq-form-variants.component';
import { RfqImageFormPopupComponent } from './rfq-image-form-popup/rfq-image-form-popup.component';
import { RfqImagePopupComponent } from './rfq-image-popup/rfq-image-popup.component';
import { RfqListComponent } from './rfq-list/rfq-list.component';
import { RfqListColumnsComponent } from './rfq-list-columns/rfq-list-columns.component';
import { RfqListRowsComponent } from './rfq-list-rows/rfq-list-rows.component';
import { RfqPricingComponent } from './rfq-pricing/rfq-pricing.component';
import { RfqPricingTableComponent } from './rfq-pricing-table/rfq-pricing-table.component';
import { RfqQuotationVariantsComponent } from './rfq-quotation-variants/rfq-quotation-variants.component';
import { RfqQuotationsComponent } from './rfq-quotations/rfq-quotations.component';
import { RfqUnitOfConversionsComponent } from './rfq-unit-of-conversions/rfq-unit-of-conversions.component';
import { RfqUnitOfConversionsPopupFormComponent } from './rfq-unit-of-conversions-popup-form/rfq-unit-of-conversions-popup-form.component';
import { RfqVariantSkusComponent } from './rfq-variant-skus/rfq-variant-skus.component';
import { SetDiscountStructureComponent } from './set-discount-structure/set-discount-structure.component';
import { SetDiscountStructureToVariantSkusComponent } from './set-discount-structure-to-variant-skus/set-discount-structure-to-variant-skus.component';
import { SetMarkupStructureComponent } from './set-markup-structure/set-markup-structure.component';
import { SetMaterialCostComponent } from './set-material-cost/set-material-cost.component';
import { TransferOrderFormComponent } from './transfer-order-form/transfer-order-form.component';
import { UpdateAlternateSkusComponent } from './update-alternate-skus/update-alternate-skus.component';
import { UpdateMasterAlternateSkuComponent } from './update-master-alternate-sku/update-master-alternate-sku.component';
import { UpdateRfqLocationsComponent } from './update-rfq-locations/update-rfq-locations.component';

const standalonePipes = [TransformKeysPipe];

@NgModule({
  exports: [
    RfqDetailsComponent,
    DefectiveReportsComponent,
    DefectiveReportsFormComponent,
    DefectiveReportsFormRfqsComponent,
    PickListTotalComponent,
  ],
  declarations: [
    QuotationRequestsComponent,
    QuotationPopupFormComponent,
    RfqDetailsComponent,
    RfqQuotationsComponent,
    NegotiatePriceFormComponent,
    RfqFactoryAdminAssignmentComponent,
    ConsolidateMasterSkuComponent,
    RfqClientProfileComponent,
    QuotationVariantFormComponent,
    RfqQuotationVariantsComponent,
    EditRfqVariantsComponent,
    RfqFormVariantsComponent,
    RfqDetailsPageComponent,
    RfqDetailsQuotationsComponent,
    QuotationMessagesComponent,
    PriceNegotiationsComponent,
    RequoteRequestsComponent,
    QuotesPageComponent,
    CreateClientRfqVariantComponent,
    RfqListComponent,
    RfqListRowsComponent,
    RfqListColumnsComponent,
    RfqDetailsSalesComponent,
    RfqDetailsCostComponent,
    UpdateRfqLocationsComponent,
    RfqDetailsCogsComponent,
    InvListComponent,
    RequestForQuotationListComponent,
    NewProductPageComponent,
    NpPipelineViewComponent,
    NpListViewComponent,
    NpRfqPipelinePopupComponent,
    RetailPricesComponent,
    ChannelShippingComponent,
    SetMaterialCostComponent,
    UpdateAlternateSkusComponent,
    UpdateMasterAlternateSkuComponent,
    StocksComponent,
    AdjustmentEntryFormComponent,
    AdjustmentEntryFormPopupComponent,
    AdjustmentEntriesComponent,
    RfqDetailsQtyLogsComponent,
    NpTagsPopupComponent,
    NdListViewComponent,
    NdPipelineViewComponent,
    NdProductPageComponent,
    NdPopupFormComponent,
    NdPipelinePopupComponent,
    NdViewDesignComponent,
    ArchivedRfqsComponent,
    UpdateMasterAlternateSkuComponent,
    StockTransferPopupFormComponent,
    StockTransfersListComponent,
    RfqVariantSkusComponent,
    SetDiscountStructureComponent,
    RfqClientsPopupFormComponent,
    SetMarkupStructureComponent,
    NdAttachmentPopupComponent,
    RfqAttachments,
    NdViewCardPopupComponent,
    NdCardNotesComponent,
    NdCardSubtasksComponent,
    RfqImagePopupComponent,
    RfqImageFormPopupComponent,
    SkuDuplicateReportComponent,
    NdMasterPipelineViewComponent,
    NdBulkUpdatePopupFormComponent,
    BulkUpdateClientRfqFormComponent,
    ExportTemplateRfqPopupFormComponent,
    ExportTemplateRfqComponent,
    AdjustmentEntryStockLocationsFormComponent,
    SkuStockDetailsComponent,
    SkuStockDetailsPurchaseOrdersComponent,
    SkuStockDetailsChannelOrdersComponent,
    ReceivingComponent,
    DefectiveReportsComponent,
    DefectiveReportsFormComponent,
    DefectiveReportsFormRfqsComponent,
    BulkTagsComponent,
    SetDiscountStructureToVariantSkusComponent,
    RfqPricingComponent,
    RfqPricingTableComponent,
    RfqUnitOfConversionsComponent,
    RfqUnitOfConversionsPopupFormComponent,
    RfqDetailsKitItemsComponent,
    ClientPriceOverridesComponent,
    PickListTotalComponent,
    InventoryTransferOrderComponent,
    TransferOrderFormComponent,
    GridStocksComponent,
  ],
  imports: [
    BlockUIModule.forRoot(),
    CommonModule,
    InventoryRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    PartialsModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatTooltipModule,
    MatDatepickerModule,
    MatDialogModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatIconModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatFileUploadModule,
    MatPaginatorModule,
    MatTabsModule,
    MatCardModule,
    MatRadioModule,
    MatTooltipModule,
    NgbModule,
    MessagingModule,
    SharedModule,
    AngularEditorModule,
    TagInputModule,
    NgSelectModule,
    DragDropModule,
    PerfectScrollbarModule,
    NgxChartsModule,
    NgxPaginationModule,
    NgPipesModule,
    MatMenuModule,
    QRCodeModule,
    NgxDocViewerModule,
    TranslateModule.forChild(),
    AtMentionModule,
    CoreModule,
    LuminousV2Module,
    FeatureModule,
    PipeModule,
    DirectiveModule,
    CostLayersModule,
    UomDisplayPipe,
    RfqKitItemsWarehouseComponent,
    ...standalonePipes,
    PricingMatrixPipe,
  ],
})
export class InventoryModule {
  constructor(
    private ngxPermissionsService :NgxPermissionsService,
    private permissionUtilService :PermissionUtilService,
    private featureToggleService :FeatureToggleService,
    private featureFlagUtilService :FeatureFlagUtilService,
  ) {
    this.featureFlagUtilService.setService(this.featureToggleService);
    this.permissionUtilService.setService(this.ngxPermissionsService);
  }
}
